/**
 * This is a wrapper component for different Layouts.
 * Navigational 'aside' content should be added to this wrapper.
 */
import React, { useEffect, useState } from 'react';

import { FormattedMessage } from '../../../util/reactIntl';

import { TabNav } from '../../../components';

import { createGlobalState } from './hookGlobalState';

import css from './LayoutSideNavigation.module.css';

const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;

// Add global state for tab scrolling effect
const initialScrollState = { scrollLeft: 0 };
const { useGlobalState } = createGlobalState(initialScrollState);

// Horizontal scroll animation using element.scrollTo()
const scrollToTab = (currentPage, scrollLeft, setScrollLeft) => {
  const el = document.querySelector(`#${currentPage}Tab`);

  if (el) {
    // el.scrollIntoView doesn't work with Safari and it considers vertical positioning too.
    // This scroll behaviour affects horizontal scrolling only
    // and it expects that the immediate parent element is scrollable.
    const parent = el.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const maxScrollDistance = parent.scrollWidth - parentRect.width;

    const hasParentScrolled = parent.scrollLeft > 0;
    const scrollPositionCurrent = hasParentScrolled ? parent.scrollLeft : scrollLeft;

    const tabRect = el.getBoundingClientRect();
    const diffLeftBetweenTabAndParent = tabRect.left - parentRect.left;
    const tabScrollPosition = parent.scrollLeft + diffLeftBetweenTabAndParent;

    const scrollPositionNew =
      tabScrollPosition > maxScrollDistance
        ? maxScrollDistance
        : parent.scrollLeft + diffLeftBetweenTabAndParent;

    const needsSmoothScroll = scrollPositionCurrent !== scrollPositionNew;

    if (parent.scrollTo && (!hasParentScrolled || (hasParentScrolled && needsSmoothScroll))) {
      // Ensure that smooth scroll animation uses old position as starting point after navigation.
      parent.scrollTo({ left: scrollPositionCurrent });
      // Scroll to new position
      parent.scrollTo({ left: scrollPositionNew, behavior: 'smooth' });
    }
    // Always keep track of new position (even if smooth scrolling is not applied)
    setScrollLeft(scrollPositionNew);
  }
};

/**
 * Account-settings navigation ("Editorial Quiet" account family): a quiet
 * "MY ACCOUNT" list at desktop, the reference's pill row below 1024px.
 *
 * Lineup: Contact details · Password · Billing & subscriptions · Manage
 * account. Payout details and Payment methods were removed (operator
 * decision: TPH runs no on-platform transactions).
 *
 * @component
 * @param {Object} props
 * @param {Object} props.accountSettingsNavProps
 * @param {string?} props.accountSettingsNavProps.currentPage
 * @returns {JSX.Element} Nav with links to different account settings pages
 */
const LayoutWrapperAccountSettingsSideNav = props => {
  const [mounted, setMounted] = useState(false);
  const [scrollLeft, setScrollLeft] = useGlobalState('scrollLeft');
  const { accountSettingsNavProps, ariaLabel } = props;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      const { currentPage } = accountSettingsNavProps;
      const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
      const hasHorizontalTabLayout = hasMatchMedia
        ? window.matchMedia(`(max-width: ${MAX_HORIZONTAL_NAV_SCREEN_WIDTH}px)`)?.matches
        : true;

      // Check if scrollToTab call is needed (tab is not visible on mobile)
      if (hasHorizontalTabLayout) {
        scrollToTab(currentPage, scrollLeft, setScrollLeft);
      }
    }
  }, [mounted]);

  const { currentPage } = accountSettingsNavProps;

  // Below 1024px the reference pills use short labels ("Contact" / "Billing");
  // the desktop rail keeps the full titles
  const pillLabel = (fullId, shortId) => (
    <>
      <span className={css.pillLabelFull}>
        <FormattedMessage id={fullId} />
      </span>
      <span className={css.pillLabelShort}>
        <FormattedMessage id={shortId} />
      </span>
    </>
  );

  const tabs = [
    {
      text: pillLabel(
        'LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle',
        'LayoutWrapperAccountSettingsSideNav.contactDetailsTabShort'
      ),
      selected: currentPage === 'ContactDetailsPage',
      id: 'ContactDetailsPageTab',
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
    {
      text: pillLabel(
        'LayoutWrapperAccountSettingsSideNav.passwordTabTitle',
        'LayoutWrapperAccountSettingsSideNav.passwordTabShort'
      ),
      selected: currentPage === 'PasswordChangePage',
      id: 'PasswordChangePageTab',
      linkProps: {
        name: 'PasswordChangePage',
      },
    },
    {
      text: pillLabel(
        'LayoutWrapperAccountSettingsSideNav.billingTabTitle',
        'LayoutWrapperAccountSettingsSideNav.billingTabShort'
      ),
      selected: currentPage === 'BillingSubscriptionsPage',
      id: 'BillingSubscriptionsPageTab',
      linkProps: {
        name: 'BillingSubscriptionsPage',
      },
    },
    {
      text: pillLabel(
        'LayoutWrapperAccountSettingsSideNav.manageAccountTabTitle',
        'LayoutWrapperAccountSettingsSideNav.manageAccountTabShort'
      ),
      selected: currentPage === 'ManageAccountPage',
      id: 'ManageAccountPageTab',
      linkProps: {
        name: 'ManageAccountPage',
      },
    },
  ];

  return (
    <>
      <div className={css.accountNavLabel}>
        <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.sectionLabel" />
      </div>
      <TabNav
        rootClassName={css.accountTabs}
        tabRootClassName={css.accountTab}
        tabs={tabs}
        skin="cream"
        ariaLabel={ariaLabel}
      />
    </>
  );
};

export default LayoutWrapperAccountSettingsSideNav;
